import { atomFamily, atomWithStorage } from 'jotai/utils';
import { atom } from 'jotai';
import request from 'lib/request';
import { createMutationAtom } from 'atoms/create-resource-atom';
import type { MapUserConfig } from 'components/map/types';
import type {
  PostDcaRegressionRequestResponse,
  DcaRegressionRequestsResponse,
  DcaRegressionProgressResponse,
  ProbitResponse,
  GetDcaRegressionRequestResponse,
  PromoteForecastsResponse,
} from 'types/dca-workspaces';
import {
  notificationsAtom,
  NotificationGroup,
  type NotificationState,
} from 'components/atomised-notifications/atoms';
import { MineralType } from 'types/minerals';

export type WellSelectionEntry = {
  selected: boolean;
  dcaForecastId: string;
  isLatestForecast: boolean;
};

export type WellSelectionMode = 'all_selected' | 'not_selected';

export type WellSelectionState = {
  mode: WellSelectionMode;
  items: Record<string, WellSelectionEntry>;
};

/** @deprecated Use WellSelectionState instead */
export type WellSelectionMap = WellSelectionState;

const INITIAL_SELECTION_STATE: WellSelectionState = {
  mode: 'all_selected',
  items: {},
};

/**
 * Returns the effective selection state for a well, falling back to the
 * mode-level default when the well has not been loaded into `items` yet.
 *
 * @param {WellSelectionState} state - The current well selection state.
 * @param {string} uwi - The unique well identifier to check.
 * @return {boolean} Whether the well is selected.
 */
export function isWellSelected(
  state: WellSelectionState,
  uwi: string
): boolean {
  const entry = state.items[uwi];
  return entry ? entry.selected : state.mode === 'all_selected';
}

/**
 * Global selected-well count for the workspace (ignores table filters).
 * Uses mode defaults for wells not yet loaded into `items`.
 *
 * @param {WellSelectionState} state - Current well selection state.
 * @param {number} totalWells - Total wells in the workspace.
 * @return {number} Count of selected wells across the full workspace.
 */
export function countSelectedWells(
  state: WellSelectionState,
  totalWells: number
): number {
  if (state.mode === 'all_selected') {
    const deselected = Object.values(state.items).filter(
      (e) => !e.selected
    ).length;
    return totalWells - deselected;
  }
  return Object.values(state.items).filter((e) => e.selected).length;
}

/**
 * Build uwis / excludeUwis query filter from global selection state.
 * Same semantics as regression and promote payloads.
 *
 * @param {WellSelectionState} state - Current well selection state.
 * @return {Object} Table query filter; may include uwis or excludeUwis arrays.
 */
export function buildWellSelectionQueryFilter(state: WellSelectionState): {
  uwis?: string[];
  excludeUwis?: string[];
} {
  if (state.mode === 'all_selected') {
    const excluded = Object.keys(state.items).filter(
      (uwi) => !state.items[uwi].selected
    );
    return excluded.length > 0 ? { excludeUwis: excluded } : {};
  }
  const included = Object.keys(state.items).filter(
    (uwi) => state.items[uwi].selected
  );
  return { uwis: included };
}

/**
 * Custom storage that transparently migrates the old flat
 * `Record<string, WellSelectionEntry>` format to the new
 * `{ mode, items }` shape on first read.
 */
const wellSelectionStorage = {
  getItem: (key: string, initialValue: WellSelectionState) => {
    const raw = localStorage.getItem(key);
    if (!raw) return initialValue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && 'mode' in parsed && 'items' in parsed) return parsed;
      return { mode: 'all_selected' as const, items: parsed };
    } catch {
      return initialValue;
    }
  },
  setItem: (key: string, value: WellSelectionState) =>
    localStorage.setItem(key, JSON.stringify(value)),
  removeItem: (key: string) => localStorage.removeItem(key),
  subscribe: (
    key: string,
    callback: (value: WellSelectionState) => void,
    initialValue: WellSelectionState
  ) => {
    const listener = (e: StorageEvent) => {
      if (e.key !== key || e.storageArea !== localStorage) return;
      if (!e.newValue) {
        callback(initialValue);
        return;
      }
      try {
        callback(JSON.parse(e.newValue));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
  },
};

/**
 * Per-well selection state persisted per workspace in localStorage.
 *
 * `mode` determines the default selection for wells not yet in `items`:
 * - `'all_selected'`:  new pages auto-select all wells
 * - `'not_selected'`:  new pages leave wells deselected
 *
 * `items` stores per-well state (checkbox + active forecast ID) for wells
 * that have been loaded so far. Toggling "select all / deselect all" flips
 * the mode and updates every existing item.
 */
export const workspaceWellSelectionMapAtomFamily = atomFamily(
  (workspaceId: number) =>
    atomWithStorage<WellSelectionState>(
      `dca-workspace-${workspaceId}-well-selection-map`,
      INITIAL_SELECTION_STATE,
      wellSelectionStorage
    )
);

const batchRegressionMutation = createMutationAtom<
  PostDcaRegressionRequestResponse,
  'batchRegression'
>();

export { batchRegressionMutation };

export const batchRegressionStatusAtom = atom<
  'idle' | 'loading' | 'success' | 'error'
>('idle');

export const regressionJobFinishedAtom = atom(false);

// ---------------------------------------------------------------------------
// Active regression request lookup + progress polling
// ---------------------------------------------------------------------------

const activeRegressionRequestDataAtom =
  atom<GetDcaRegressionRequestResponse | null>(null);

function buildActiveJobInfoState(
  current: NotificationState,
  jobId: number
): NotificationState {
  const withoutSuccess = { ...current };
  delete withoutSuccess[NotificationGroup.DcaRegression];
  return {
    ...withoutSuccess,
    [NotificationGroup.DcaActiveJob]: [
      {
        id: `active-job-${jobId}-info`,
        title: 'Info',
        message: `Bulk DCA Request (Job - ID ${jobId}) is currently processing. Actions are temporarily unavailable until request completes.`,
        kind: 'info',
        hideCloseButton: true,
        timestamp: Date.now(),
      },
    ],
  };
}

export const activeRegressionRequestAtom = atom(
  (get) => get(activeRegressionRequestDataAtom),
  async (get, set, workspaceId: number) => {
    const listRes = (await request.get(
      `/dca_workspaces/${workspaceId}/regression_requests?active=true`
    )) as DcaRegressionRequestsResponse | { error: string };

    if ('error' in listRes) throw new Error(listRes.error);

    const first = listRes.results?.[0];
    if (!first) {
      set(activeRegressionRequestDataAtom, null);
      return null;
    }

    const detailRes = (await request.get(first.href)) as
      GetDcaRegressionRequestResponse | { error: string };

    if ('error' in detailRes) throw new Error(detailRes.error);

    set(activeRegressionRequestDataAtom, detailRes);
    set(batchRegressionStatusAtom, 'loading');
    set(regressionJobFinishedAtom, false);
    set(regressionProgressDataAtom, null);
    set(
      notificationsAtom,
      buildActiveJobInfoState(get(notificationsAtom), detailRes.id)
    );
    return detailRes;
  }
);

export const setActiveRegressionRequestAtom = atom(
  null,
  async (get, set, postData: PostDcaRegressionRequestResponse) => {
    const detailRes = (await request.get(postData.href)) as
      GetDcaRegressionRequestResponse | { error: string };

    if ('error' in detailRes) throw new Error(detailRes.error);

    set(activeRegressionRequestDataAtom, detailRes);
    set(batchRegressionStatusAtom, 'loading');
    set(regressionJobFinishedAtom, false);
    set(regressionProgressDataAtom, null);
    set(
      notificationsAtom,
      buildActiveJobInfoState(get(notificationsAtom), detailRes.id)
    );
  }
);

export const cancelRegressionRequestAtom = atom(null, async (get, set) => {
  const activeRequest = get(activeRegressionRequestDataAtom);
  if (!activeRequest) return;

  const cancelUrl = `${activeRequest.href}`;
  const res = await request.patch(
    cancelUrl,
    {
      status: 'cancelled',
    },
    new AbortController()
  );

  if (res.type !== 'success') {
    const current = get(notificationsAtom);
    const existing = current[NotificationGroup.DcaActiveJob] ?? [];
    set(notificationsAtom, {
      ...current,
      [NotificationGroup.DcaActiveJob]: [
        ...existing,
        {
          id: `cancel-job-${activeRequest.id}-error-${Date.now()}`,
          title: 'Error',
          message: `Failed to cancel Bulk DCA Request (Job - ID ${activeRequest.id})`,
          kind: 'error' as const,
          timestamp: Date.now(),
          autoHideMs: 4_000,
        },
      ],
    });
    return;
  }

  const jobId = activeRequest.id;
  set(activeRegressionRequestDataAtom, null);
  set(regressionProgressDataAtom, null);
  set(regressionJobFinishedAtom, false);
  set(batchRegressionStatusAtom, 'idle');
  const curr = get(notificationsAtom);
  const rest = { ...curr };
  delete rest[NotificationGroup.DcaActiveJob];
  const existing = rest[NotificationGroup.DcaWorkspaceUpdate] ?? [];
  rest[NotificationGroup.DcaWorkspaceUpdate] = [
    ...existing,
    {
      id: `cancel-job-${jobId}-success-${Date.now()}`,
      title: 'Cancelled',
      message: `Bulk DCA Request (Job - ID ${jobId}) has been cancelled successfully.`,
      kind: 'success' as const,
      timestamp: Date.now(),
      autoHideMs: 4_000,
    },
  ];
  set(notificationsAtom, rest);
});

export const regressionJobCompletedAtom = atom(null, (get, set) => {
  const activeRequest = get(activeRegressionRequestDataAtom);
  const progress = get(regressionProgressDataAtom);

  const jobId = activeRequest?.id ?? '';
  const totalWells = progress?.total ?? 0;

  const current = get(notificationsAtom);
  const withoutInfo = { ...current };
  delete withoutInfo[NotificationGroup.DcaActiveJob];
  set(notificationsAtom, {
    ...withoutInfo,
    [NotificationGroup.DcaRegression]: [
      {
        id: `regression-${jobId}-success`,
        title: 'Finished:',
        message: `Successfully Completed Bulk DCA Request (Job - ID ${jobId}) for ${totalWells} Wells`,
        kind: 'success' as const,
        timestamp: Date.now(),
        autoHideMs: 4_000,
      },
    ],
  });

  set(regressionJobFinishedAtom, true);
  set(batchRegressionStatusAtom, 'success');
});

export const regressionProgressDataAtom =
  atom<DcaRegressionProgressResponse | null>(null);

export const regressionProgressAtom = atom(
  (get) => get(regressionProgressDataAtom),
  async (_get, set, href: string) => {
    const res = (await request.get(href)) as
      DcaRegressionProgressResponse | { error: string };

    if ('error' in res) throw new Error(res.error);

    const isActive = res.status === 'pending' || res.status === 'processing';
    set(regressionProgressDataAtom, res);
    return !isActive;
  }
);

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

export const mapConfigAtom = atom<MapUserConfig>({
  wells: {
    checked: false,
    options: {
      surfaceHole: false,
      bottomHole: false,
      stick: false,
      survey: false,
      vertical: false,
    },
  },
  wellsV2: {
    checked: false,
    options: {
      vertical: false,
      horizontal: false,
      horizontalPermit: false,
      horizontalDuc: false,
      horizontalProducing: false,
      horizontalShutIn: false,
      horizontalExpiredPermit: false,
      horizontalInactive: false,
      horizontalPrePermit: false,
      horizontalApd: false,
      horizontalProposal: false,
      horizontalPlanned: false,
      verticalPermit: false,
      verticalDuc: false,
      verticalProducing: false,
      verticalShutIn: false,
      verticalExpiredPermit: false,
      verticalInactive: false,
      verticalPrePermit: false,
      verticalApd: false,
      verticalProposal: false,
      verticalPlanned: false,
      surfaceHole: false,
      bottomHole: false,
      stick: false,
      survey: false,
      permit: false,
      duc: false,
      producing: false,
      shutIn: false,
      expiredPermit: false,
      inactive: false,
      prePermit: false,
      apd: false,
      proposal: false,
      planned: false,
      showWellNames: false,
    },
  },
  developmentAreas: {
    checked: false,
    options: {
      horizontalUnits: false,
      verticalUnits: false,
      fieldUnits: false,
      units: false,
      pricing: false,
    },
  },
  basinTier: {
    checked: false,
    options: {
      basin: false,
      tier: false,
    },
  },
  typeCurves: { checked: false },
  scrollZoom: { checked: false },
  popups: { checked: true },
  focusAreas: { checked: false, selections: {} },
  titleEntityAssets: {
    checked: false,
    options: {
      surfaceInterest: false,
      workingInterest: false,
      unleased: false,
      royaltyInterest: false,
    },
  },
  portfolioAssets: {
    checked: false,
  },
  pipelines: {
    checked: false,
    options: {
      gasOperating: false,
      gasUnderConstruction: false,
      gasPlannedOther: false,
      oilOperating: false,
      oilUnderConstruction: false,
      oilPlannedOther: false,
      productOperating: false,
      productConstruction: false,
      productPlannedOther: false,
      unknownOperating: false,
      unknownUnderConstruction: false,
      unknownPlannedOther: false,
    },
  },
  drillingRigs: {
    checked: false,
    options: {
      horizontalDrilling: false,
      horizontalDown: false,
      horizontalNotReporting: false,
      horizontalNotTracking: false,
      horizontalUnreportedStatus: false,
      verticalDrilling: false,
      verticalDown: false,
      verticalNotReporting: false,
      verticalNotTracking: false,
      verticalUnreportedStatus: false,
    },
  },
});

/**
 * Set to the UWI of the well whose forecast was just saved from inside
 * the DCA modal.  `DcaWorkspaceContent` watches this to refetch wells
 * and force-update the saved well's `dcaForecastId` in localStorage.
 * Reset to `null` after the update is applied.
 */
export const dcaModalForecastSavedAtom = atom<string | null>(null);

export const wellsLayerVisibilityAtom = atom(true);
export const wellsLayerFittedAtom = atom(false);
export const mapVisibleUwisAtom = atom<string[]>([]);

// ---------------------------------------------------------------------------
// Probit plot data fetching
// ---------------------------------------------------------------------------

export type ProbitMetric =
  'eur' | 'scaled_eur' | 'eur_per_ft' | 'dca_params' | 'cumulatives';
export type DcaParamKey = 'qi' | 'b_factor' | 'di';

const PROBIT_PLOT_URL = '/queries/probit_plot';

export type ProbitPlotState = {
  data: ProbitResponse | null;
  loading: boolean;
  error: string | null;
};

export const probitPlotDataAtomFamily = atomFamily((_stream: MineralType) =>
  atom<ProbitPlotState>({ data: null, loading: false, error: null })
);

export const fetchProbitPlotAtom = atom(
  null,
  async (
    _get,
    set,
    params: {
      stream: MineralType;
      workspaceId: number;
      dcaScenarioId: number;
      metric: ProbitMetric;
      paramKey?: DcaParamKey;
      uwis?: string[];
      excludeUwis?: string[];
      wellsWithSpecificVersionIds?: {
        uwi: string;
        wellDcaForecastId: number;
      }[];
      filters?: Record<string, unknown>;
    }
  ) => {
    const stateAtom = probitPlotDataAtomFamily(params.stream);
    set(stateAtom, (prev) => ({ ...prev, loading: true, error: null }));

    try {
      const body: Record<string, unknown> = {
        workspaceId: params.workspaceId,
        dcaScenarioId: params.dcaScenarioId,
        metric: params.metric,
      };
      if (params.metric === 'dca_params' && params.paramKey) {
        body.paramKey = params.paramKey;
      }
      if (params.excludeUwis && params.excludeUwis.length > 0) {
        body.excludeUwis = params.excludeUwis;
      } else if (params.uwis) {
        body.uwis = params.uwis;
      }
      if (params.wellsWithSpecificVersionIds?.length) {
        body.wellsWithSpecificVersionIds = params.wellsWithSpecificVersionIds;
      }
      if (params.filters && Object.keys(params.filters).length > 0) {
        body.filters = params.filters;
      }

      const res = await request.post<ProbitResponse, unknown>(
        PROBIT_PLOT_URL,
        body,
        new AbortController()
      );
      if (res.type === 'abort') return;
      if (res.type !== 'success') throw new Error('Request failed');
      set(stateAtom, { data: res.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      set(stateAtom, (prev) => ({ ...prev, loading: false, error: message }));
    }
  }
);

const promoteWellsMutation = createMutationAtom<
  PromoteForecastsResponse,
  'promoteWells'
>();

export { promoteWellsMutation };
